import {MultiFanCoordinates} from "./multiFanNoise";

/**
 * Reprezentuje partycypację pojedynczego wentylatora w obliczeniach hałasu
 */
export type MultiFanNoiseFanParticipation = {
    readonly pwlDba: number;
    readonly fanNumber: number;
    readonly absoluteFanCenterCoordinates: MultiFanCoordinates;
};

/**
 * Parametry wejściowe do obliczeń hałasu
 */
export type MultiFanNoiseCalculationParams = {
    fanParticipations: MultiFanNoiseFanParticipation[];
    observerCoordinates: MultiFanCoordinates;
    coolerWidthMeters: number;
    coolerHeightMeters: number;
    fanPwlDba: number;
    additionalUserGivenFanPwlDba: number;
};

/**
 * Wynik obliczeń dla pojedynczego wentylatora
 */
export type MultiFanNoiseFanResult = {
    fanNumber: number;
    pwlDba: number;
    coordinates: MultiFanCoordinates;
    radius2D: number;
    distance3D: number;
    splDba: number;
};

/**
 * Pełny wynik obliczeń hałasu
 */
export type MultiFanNoiseResults = {
    fanResults: MultiFanNoiseFanResult[];
    splTotalDba: number;
    odlegloscPomiarowaOdObwiedniPoX: number;
    odlegloscPomiarowaOdObwiedniPoY: number;
    sumOfFanPwlAndUserGivenFanPwlDba: number;
};

/**
 * Oblicza odległość 2D (promień) między dwoma punktami (tylko X i Y)
 */
function calculate2DDistance(point1: MultiFanCoordinates, point2: MultiFanCoordinates): number {
    const dx = point2.xMeters - point1.xMeters;
    const dy = point2.yMeters - point1.yMeters;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Oblicza odległość 3D między dwoma punktami
 */
function calculate3DDistance(point1: MultiFanCoordinates, point2: MultiFanCoordinates): number {
    const dx = point2.xMeters - point1.xMeters;
    const dy = point2.yMeters - point1.yMeters;
    const dz = point2.zMeters - point1.zMeters;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Oblicza SPL (Sound Pressure Level) dla pojedynczego wentylatora
 * Formuła: SPL = PWL - 10*LOG(2*PI*R'^2)
 * gdzie R' to odległość 3D
 */
function calculateSPL(pwlDba: number, distance3D: number): number {
    if (distance3D === 0) {
        return pwlDba; // Jeśli odległość = 0, zwróć PWL
    }
    return pwlDba - 10 * Math.log10(2 * Math.PI * distance3D * distance3D);
}

/**
 * Oblicza całkowity SPL ze wszystkich wentylatorów
 * Formuła: SPL_TOT = 10*LOG(SUM(10^(0.1*SPL_i)))
 */
function calculateTotalSPL(splValues: number[]): number {
    if (splValues.length === 0) {
        return 0;
    }
    const sum = splValues.reduce((acc, spl) => acc + Math.pow(10, 0.1 * spl), 0);
    return 10 * Math.log10(sum);
}

/**
 * Główna funkcja obliczająca hałas od wszystkich wentylatorów
 */
export function calculateMultiFanNoise(params: MultiFanNoiseCalculationParams): MultiFanNoiseResults {
    const fanResults: MultiFanNoiseFanResult[] = params.fanParticipations.map(fan => {
        const radius2D = calculate2DDistance(fan.absoluteFanCenterCoordinates, params.observerCoordinates);
        const distance3D = calculate3DDistance(fan.absoluteFanCenterCoordinates, params.observerCoordinates);
        const splDba = calculateSPL(fan.pwlDba, distance3D);

        return {
            fanNumber: fan.fanNumber,
            pwlDba: fan.pwlDba,
            coordinates: fan.absoluteFanCenterCoordinates,
            radius2D,
            distance3D,
            splDba
        };
    });

    const splValues = fanResults.map(r => r.splDba);
    const splTotalDba = calculateTotalSPL(splValues);

    const odlegloscPomiarowaOdObwiedniPoX = params.observerCoordinates.xMeters - params.coolerWidthMeters / 2;
    const odlegloscPomiarowaOdObwiedniPoY = params.observerCoordinates.yMeters - params.coolerHeightMeters / 2;
    const sumOfFanPwlAndUserGivenFanPwlDba = 10 * Math.log10(
        Math.pow(10, 0.1 * params.fanPwlDba) + Math.pow(10, 0.1 * params.additionalUserGivenFanPwlDba)
    );

    return {
        fanResults,
        splTotalDba,
        odlegloscPomiarowaOdObwiedniPoX,
        odlegloscPomiarowaOdObwiedniPoY,
        sumOfFanPwlAndUserGivenFanPwlDba
    };
}
